import { Link } from "react-router-dom";
import {
  ShieldCheck, Search, AlertTriangle, FileCheck2,
  ArrowRight, MessageSquare, MapPin, Car, Calendar, Wallet, Check, Minus,
} from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import SEO from "@/components/SEO";
import HomeHero from "@/components/HomeHero";
import ProductWalkthrough from "@/components/ProductWalkthrough";
import CachePublicShell from "@/components/CachePublicShell";
import { organizationJsonLd, websiteJsonLd } from "@/lib/seo";

/**
 * Client-facing homepage: rebuilt 2026-08-27 around one behavioral goal
 * rather than a features pitch: make checking a booking before paying or
 * travelling feel as normal as checking a flight ticket before the
 * airport. Section order below matches the homepage brief 1:1 (hero
 * through final CTA), with the interactive product demo (ProductWalkthrough)
 * kept exactly where it was, unmodified in its own internals.
 */
export default function Home() {
  const { t } = useT();
  return (
    <div data-testid="public-home">
      <CachePublicShell />
      <SEO
        title="ConfirmPH: Rental Booking Confirmation & Safety Tools in the Philippines"
        description="ConfirmPH helps renters and rental businesses manage booking confirmations, reminders, rental records, inspections, and clearer rental communication in the Philippines."
        canonical="/"
        jsonLd={[organizationJsonLd(), websiteJsonLd()]}
      />

      {/* 1-2. Hero + chat-vs-record visual */}
      <HomeHero />

      {/* 3. The standard: four questions to test a booking against */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("home.standard.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("home.standard.title")}</h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="home-standard-cards">
            <StandardCard icon={Car} title={t("home.standard.q1.t")} desc={t("home.standard.q1.d")} />
            <StandardCard icon={Calendar} title={t("home.standard.q2.t")} desc={t("home.standard.q2.d")} />
            <StandardCard icon={Wallet} title={t("home.standard.q3.t")} desc={t("home.standard.q3.d")} />
            <StandardCard icon={MapPin} title={t("home.standard.q4.t")} desc={t("home.standard.q4.d")} />
          </div>
          <p className="mt-10 text-center font-display text-xl sm:text-2xl font-semibold max-w-2xl mx-auto">
            {t("home.standard.payoff")}
          </p>
          <div className="mt-6 text-center">
            <Link to="/find-booking" data-testid="home-standard-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-semibold">
              {t("home.cta.check_booking")}
            </Link>
          </div>
        </div>
      </section>

      {/* 4. The consequence: why check before you go */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("home.consequence.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("home.consequence.title")}</h2>
          </div>
          <ol className="mt-10 grid md:grid-cols-3 gap-5" data-testid="home-consequence-cards">
            <ConsequenceCard n={1} tone="emerald" title={t("home.consequence.1.t")} desc={t("home.consequence.1.d")} />
            <ConsequenceCard n={2} tone="amber" title={t("home.consequence.2.t")} desc={t("home.consequence.2.d")} />
            <ConsequenceCard n={3} tone="rose" title={t("home.consequence.3.t")} desc={t("home.consequence.3.d")} />
          </ol>
          <p className="mt-10 text-center font-display text-xl sm:text-2xl font-semibold max-w-2xl mx-auto">
            {t("home.consequence.payoff")}
          </p>
        </div>
      </section>

      {/* 5. The mechanism: how ConfirmPH works */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("home.flow.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("home.flow.title")}</h2>
          </div>
          <ol className="mt-10 grid md:grid-cols-3 gap-5" data-testid="home-flow-steps">
            {[
              [MessageSquare, t("home.flow.1.t"), t("home.flow.1.d")],
              [FileCheck2,    t("home.flow.2.t"), t("home.flow.2.d")],
              [ShieldCheck,   t("home.flow.3.t"), t("home.flow.3.d")],
            ].map(([Icon, title, desc], i) => (
              <li key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#1B4332] text-white grid place-items-center font-display font-semibold text-sm">
                    {i + 1}
                  </div>
                  <Icon className="w-5 h-5 text-slate-700" strokeWidth={1.8} />
                </div>
                <div className="mt-3 font-display text-lg font-semibold">{title}</div>
                <div className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</div>
              </li>
            ))}
          </ol>
          <p className="mt-10 text-center font-display text-xl sm:text-2xl font-semibold max-w-2xl mx-auto">
            {t("home.flow.payoff")}
          </p>
          <p className="mt-3 text-center text-sm text-slate-500 max-w-xl mx-auto">
            {t("home.flow.note")}
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/features/renters" data-testid="home-renter-features" className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50">
              Explore renter features <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/features" data-testid="home-all-features" className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50">
              View all features <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* 6. Product proof: the interactive demo. Kept in place, internals
          untouched; only its own header copy was updated (see the
          component) to match this page's new language. */}
      <ProductWalkthrough />

      {/* 7. Trust through limitation */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <h2 className="text-center font-display text-3xl sm:text-4xl font-bold">{t("home.trust.title")}</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-6">
              <div className="flex items-center gap-2 text-emerald-800">
                <div className="w-6 h-6 rounded-full bg-emerald-100 grid place-items-center flex-shrink-0"><Check className="w-3.5 h-3.5" /></div>
                <span className="font-display font-semibold">{t("home.trust.does.t")}</span>
              </div>
              <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t("home.trust.does.d")}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-[#FAFAFA] p-6">
              <div className="flex items-center gap-2 text-slate-600">
                <div className="w-6 h-6 rounded-full bg-slate-200 grid place-items-center flex-shrink-0"><Minus className="w-3.5 h-3.5" /></div>
                <span className="font-display font-semibold text-slate-800">{t("home.trust.doesnot.t")}</span>
              </div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{t("home.trust.doesnot.d")}</p>
            </div>
          </div>
          <p className="mt-8 text-center text-sm text-slate-600 leading-relaxed max-w-xl mx-auto">
            {t("home.trust.footer")}
          </p>
        </div>
      </section>

      {/* 8. The habit, before pickup */}
      <section className="bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("home.habit.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("home.habit.title")}</h2>
          </div>
          <ul className="mt-8 max-w-xl mx-auto space-y-2.5" data-testid="home-habit-list">
            {[t("home.habit.1"), t("home.habit.2"), t("home.habit.3"), t("home.habit.6"), t("home.habit.4"), t("home.habit.5")].map((line, i) => (
              <li key={i} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-800">
                <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-800 grid place-items-center flex-shrink-0">
                  <Check className="w-3 h-3" />
                </div>
                {line}
              </li>
            ))}
          </ul>
          <div className="mt-8 text-center">
            <Link to="/find-booking" data-testid="home-habit-cta" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-semibold">
              {t("home.cta.check_booking")}
            </Link>
          </div>
          <p className="mt-6 text-center font-display text-xl sm:text-2xl font-bold text-[#1B4332]">
            {t("home.habit.mantra")}
          </p>
        </div>
      </section>

      {/* 9. Still looking: discovery folded into the same journey */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("home.discover.eyebrow")}</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("home.discover.title")}</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">{t("home.discover.subtitle")}</p>
            <Link to="/rentals" data-testid="home-discover-cta" className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-lg btn-brand font-semibold">
              <Search className="w-4 h-4" /> {t("home.cta.discover")}
            </Link>
            <p className="mt-6 text-xs sm:text-sm text-slate-500 font-mono" data-testid="home-discover-journey">
              {t("home.discover.journey")}
            </p>
          </div>
        </div>
      </section>

      {/* 10. Final CTA */}
      <section className="bg-[#10241B] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
            {t("home.final.title.a")}
            <br />{t("home.final.title.b")}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl mx-auto">
            {t("home.final.body")}
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/find-booking" data-testid="home-final-cta-primary" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-[#10241B] font-semibold hover:bg-white/90">
              {t("home.cta.check_booking")}
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-white/70">
            <span className="inline-flex items-center gap-2">
              {t("home.final.secondary.label")}{" "}
              <Link to="/request-confirmation" data-testid="home-final-cta-secondary" className="font-medium text-white hover:underline">
                {t("home.cta.get_in_writing")}
              </Link>
            </span>
            <span className="inline-flex items-center gap-2">
              {t("home.cta.still_looking")}{" "}
              <Link to="/rentals" data-testid="home-final-cta-tertiary" className="font-medium text-white hover:underline inline-flex items-center gap-1">
                {t("home.cta.discover")} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </span>
          </div>
        </div>
      </section>

      {/* Business crossover: unchanged from before this rewrite. */}
      <section className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <aside className="rounded-2xl border border-slate-200 bg-[#FAFAFA] p-6 sm:p-8" data-testid="home-business-bridge">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{t("home.business.bridge.eyebrow")}</div>
            <h3 className="mt-2 font-display text-2xl font-semibold">{t("home.business.bridge.title")}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed max-w-xl">{t("home.business.bridge.subtitle")}</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/businesses" data-testid="home-business-bridge-cta" className="inline-flex min-h-11 items-center gap-2 px-5 py-2.5 rounded-lg btn-brand font-medium text-sm">
                {t("home.business.bridge.cta")} <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/features/businesses" data-testid="home-business-features" className="inline-flex min-h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50">
                Explore business features <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function StandardCard({ icon: Icon, title, desc }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="w-9 h-9 rounded-lg bg-emerald-50 text-[#1B4332] grid place-items-center">
        <Icon className="w-5 h-5" strokeWidth={1.8} />
      </div>
      <h3 className="mt-3 font-display text-lg font-semibold">{title}</h3>
      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{desc}</p>
    </div>
  );
}

const CONSEQUENCE_TONES = {
  emerald: "bg-emerald-600",
  amber: "bg-amber-500",
  rose: "bg-rose-600",
};

function ConsequenceCard({ n, tone, title, desc }) {
  return (
    <li className="rounded-xl border border-slate-200 bg-white p-5">
      <div className={`w-8 h-8 rounded-lg ${CONSEQUENCE_TONES[tone]} text-white grid place-items-center font-display font-semibold text-sm`}>
        {n}
      </div>
      <div className="mt-3 font-display text-lg font-semibold">{title}</div>
      <div className="mt-1 text-sm text-slate-600 leading-relaxed">{desc}</div>
    </li>
  );
}
